import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import clsx from "clsx";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { useCurrentUser, userIsBuero } from "@/lib/auth";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useRealtime } from "@/lib/useRealtime";
import {
  realtimeEventConcernsOrder,
  useTrailingCallback,
} from "@/projectOverview/realtimeOrderEvent";
import { useTimesheetFlow } from "@/features/timesheetFlow/useTimesheetFlow";
import { EditProjectDialog } from "@/projectOverview/EditProjectDialog";
import { buildProjectOverviewPanels } from "@/projectOverview/ProjectOverviewPanels";
import { ensureOverviewGroups, userCanManageMontageunterlagen } from "@/projectOverview/permissions";
import { useProjectOverviewData } from "@/projectOverview/useProjectOverviewData";
import { ProjectDetailNav } from "@/pages/projekte/ProjectDetailNav";
import { countProjectCompletions, ProjectCompletionsTab } from "@/pages/projekte/ProjectCompletionsTab";
import { countProjectOrders, ProjectOrdersTab } from "@/pages/projekte/ProjectOrdersTab";
import {
  countProjectDocuments,
  ProjectDocumentsTab,
} from "@/pages/projekte/ProjectDocumentsTab";
import { ProjectAngebotTab } from "@/pages/projekte/ProjectAngebotTab";
import { getHeroDocuments } from "@/lib/projectOverview";
import {
  projectExecutionClassBadgeClass,
  projectWorkflowStatus,
  projectWorkflowStatusBadgeClass,
} from "@/lib/projects";
import { ProjectKorrekturBanner } from "@/projectOverview/ProjectKorrekturBanner";
import { OrdnerJahrBadge } from "@/projectOverview/OrdnerJahrBadge";
import { ProjectExecutionClassControl } from "@/projectOverview/ProjectExecutionClassControl";
import { ProjectWorkflowStatusControl } from "@/projectOverview/ProjectWorkflowStatusControl";
import { useFestpreisProject } from "@/projectOverview/useFestpreisProject";
import {
  parseProjectDetailTab,
  type ProjectDetailTab,
} from "@/pages/projekte/projectDetailTabs";

function parseOrderId(raw: string | undefined): number | null {
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function CompletionsOverviewHint({ onOpen }: { onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="eh-project-orders-hint w-full rounded-ios-lg border border-separator bg-card px-4 py-3 text-left transition hover:bg-fill-tertiary/60"
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">
        Fertigstellungsmeldungen
      </p>
      <p className="mt-1 text-sm text-label">
        Festpreis-Projekt — Meldungen und PDF im Tab{" "}
        <span className="font-semibold text-accent">Fertigstellungen</span>.
      </p>
    </button>
  );
}
function DocumentsOverviewHint({
  count,
  onOpen,
}: {
  count: number;
  onOpen: () => void;
}) {
  if (count <= 0) return null;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="eh-project-orders-hint w-full rounded-ios-lg border border-separator bg-card px-4 py-3 text-left transition hover:bg-fill-tertiary/60"
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">Dokumente</p>
      <p className="mt-1 text-sm text-label">
        {count} {count === 1 ? "Dokument" : "Dokumente"} mit allen Hero-Details im Tab{" "}
        <span className="font-semibold text-accent">Dokumente</span>.
      </p>
    </button>
  );
}

function OrdersOverviewHint({
  count,
  onOpen,
}: {
  count: number;
  onOpen: () => void;
}) {
  if (count <= 0) return null;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="eh-project-orders-hint w-full rounded-ios-lg border border-separator bg-card px-4 py-3 text-left transition hover:bg-fill-tertiary/60"
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">Bestellungen</p>
      <p className="mt-1 text-sm text-label">
        {count} {count === 1 ? "Bestellung" : "Bestellungen"} mit ausführlichen Details im Tab{" "}
        <span className="font-semibold text-accent">Bestellungen</span>.
      </p>
    </button>
  );
}

export function ProjectDetailPage() {
  const { orderId: orderIdParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const orderId = parseOrderId(orderIdParam);
  const { user } = useCurrentUser();
  const { refreshKey } = useTimesheetFlow();
  const isMobile = useMediaQuery("(max-width: 1023px)");
  const { data, loading, error, refetch } = useProjectOverviewData(orderId, !!orderId, true);
  const [groups, setGroups] = useState<string[]>([]);
  const [editOpen, setEditOpen] = useState(false);
  const [documentCount, setDocumentCount] = useState(0);
  const [stuecklisteEditorOpen, setStuecklisteEditorOpen] = useState(false);
  const stuecklisteEditorOpenRef = useRef(false);
  const pendingRefetchRef = useRef(false);
  stuecklisteEditorOpenRef.current = stuecklisteEditorOpen;

  const scheduleRefetch = useTrailingCallback(() => {
    void refetch();
  }, 250);

  const requestRefetch = useCallback(() => {
    if (stuecklisteEditorOpenRef.current) {
      pendingRefetchRef.current = true;
      return;
    }
    scheduleRefetch();
  }, [scheduleRefetch]);

  useEffect(() => {
    if (stuecklisteEditorOpen || !pendingRefetchRef.current) return;
    pendingRefetchRef.current = false;
    void refetch();
  }, [stuecklisteEditorOpen, refetch]);

  const isBuero = userIsBuero(user);
  const canManageExc = userCanManageMontageunterlagen(user);
  const activeTab = parseProjectDetailTab(searchParams.get("tab"));
  const effectiveTab =
    (activeTab === "dokumente" || activeTab === "angebot") && !isBuero
      ? ("uebersicht" as ProjectDetailTab)
      : activeTab;

  const setActiveTab = useCallback(
    (tab: ProjectDetailTab) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (tab === "uebersicht") next.delete("tab");
          else next.set("tab", tab);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useEffect(() => {
    void ensureOverviewGroups(user).then(setGroups);
  }, [user]);

  useEffect(() => {
    if (!orderId) return;
    void refetch();
  }, [orderId, refetch, refreshKey]);

  useRealtime({
    scopes: [
      "auftraege:list",
      `auftraege:${orderId}`,
      `project:${orderId}`,
      `projects:${orderId}`,
    ],
    domains: ["auftraege", "projects", "hero", "timesheets", "dokumente"],
    events: [
      "auftraege_changed",
      "projects_changed",
      "hero_changed",
      "hero_custom_fields_updated",
      "timesheets_changed",
      "dokumente_changed",
      "dokument_status_changed",
    ],
    enabled: !!orderId,
    onChange: (payload) => {
      if (!realtimeEventConcernsOrder(payload, orderId)) return;
      requestRefetch();
    },
  });

  const panels = useMemo(() => {
    if (!data) return null;
    return buildProjectOverviewPanels({
      data,
      user,
      groups,
      onRefetch: requestRefetch,
      onEdit: () => setEditOpen(true),
      forceBueroSections: true,
      includeOrders: false,
      includeHeroDocuments: false,
      includeAppDocuments: false,
      pageLayout: true,
      onStuecklisteEditorOpenChange: setStuecklisteEditorOpen,
    });
  }, [data, user, groups, requestRefetch]);

  const festpreisMode = useFestpreisProject(data?.project ?? null);

  const orderCount = data ? countProjectOrders(data.orders) : 0;
  const completionCount = data
    ? countProjectCompletions(data.project, data.timesheets, festpreisMode)
    : 0;
  useEffect(() => {
    if (!orderId || !isBuero) {
      setDocumentCount(0);
      return;
    }
    let cancelled = false;
    void getHeroDocuments(orderId)
      .then((list) => {
        if (!cancelled) setDocumentCount(countProjectDocuments(Array.isArray(list) ? list : []));
      })
      .catch(() => {
        if (!cancelled) setDocumentCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId, isBuero, refreshKey]);

  const navCounts = useMemo(
    () => ({
      bestellungen: orderCount,
      fertigstellungen: completionCount,
      dokumente: isBuero ? documentCount : undefined,
      notizen: data?.notes?.length ?? 0,
    }),
    [orderCount, completionCount, documentCount, isBuero, data?.notes?.length],
  );

  const title = data?.project.auftragsnummer
    ? `Projekt ${data.project.auftragsnummer}`
    : orderId
      ? `Projekt #${orderId}`
      : "Projekt";

  const subtitle = data ? (
    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
      {data.project.kunde ? <span>{data.project.kunde}</span> : null}
      {data.project.kunde && data.project.ort ? <span aria-hidden>·</span> : null}
      {data.project.ort ? <span>{data.project.ort}</span> : null}
      {(() => {
        const workflow = projectWorkflowStatus(data.project);
        const exc = String(data.project.execution_class || "").trim().toUpperCase();
        return (
          <span className="eh-wf-badge-stack">
            <span className={clsx("text-xs", projectWorkflowStatusBadgeClass(workflow.key))}>
              {workflow.label}
            </span>
            {workflow.secondary ? (
              <span
                className={clsx(
                  "text-xs",
                  projectWorkflowStatusBadgeClass(workflow.secondary.key),
                )}
              >
                {workflow.secondary.label}
              </span>
            ) : null}
            {exc === "EXC2" ? (
              <span className={clsx("text-xs", projectExecutionClassBadgeClass(exc))}>EXC2</span>
            ) : null}
          </span>
        );
      })()}
    </span>
  ) : undefined;

  const tabContent = data && panels ? (
    <>
      {effectiveTab === "uebersicht" ? (
        <div className="space-y-3">
          <OrdersOverviewHint count={orderCount} onOpen={() => setActiveTab("bestellungen")} />
          {isBuero ? (
            <DocumentsOverviewHint count={documentCount} onOpen={() => setActiveTab("dokumente")} />
          ) : null}
          {festpreisMode ? (
            <CompletionsOverviewHint onOpen={() => setActiveTab("fertigstellungen")} />
          ) : null}
          {panels.main}
        </div>
      ) : null}
      {effectiveTab === "bestellungen" ? (
        <ProjectOrdersTab
          auftragId={data.project.id}
          auftragsnummer={data.project.auftragsnummer}
          materialLines={data.orders}
          project={data.project}
        />
      ) : null}
      {effectiveTab === "fertigstellungen" ? (
        <ProjectCompletionsTab
          project={data.project}
          timesheets={data.timesheets}
          user={user}
          groups={groups}
        />
      ) : null}
      {effectiveTab === "angebot" && isBuero ? (
        <ProjectAngebotTab orderId={data.project.id} />
      ) : null}
      {effectiveTab === "dokumente" && isBuero ? (
        <ProjectDocumentsTab
          orderId={data.project.id}
          heroProjectId={data.project.hero_project_id}
        />
      ) : null}
      {effectiveTab === "notizen" && isMobile ? (
        <div className="eh-project-page-notes eh-project-page-notes--mobile">{panels.notes}</div>
      ) : null}
    </>
  ) : null;

  if (!orderId) {
    return (
      <AppShell title="Projekt" back={{ to: "/projekte", label: "Projekte" }}>
        <Alert variant="error">Ungültige Projekt-ID in der URL.</Alert>
      </AppShell>
    );
  }

  return (
    <>
      <AppShell
        title={title}
        subtitle={subtitle}
        back={{ to: "/projekte", label: "Projekte" }}
        actions={
          data ? (
            <Button variant="secondary" size="sm" onClick={() => void refetch()} loading={loading}>
              Aktualisieren
            </Button>
          ) : null
        }
        className="eh-po-page"
      >
        {loading && !data ? (
          <div className="flex items-center justify-center py-20">
            <Spinner />
            <span className="ml-2 text-sm text-label-secondary">Lade Projekt …</span>
          </div>
        ) : error && !data ? (
          <Alert variant="error">{error}</Alert>
        ) : data && panels ? (
          <>
            <div className="eh-po-page-hero rounded-ios-lg border border-separator bg-card px-4 py-4 sm:px-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 space-y-1">
                  <p className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">
                    Projektübersicht
                  </p>
                  <h2 className="flex min-w-0 flex-wrap items-center gap-2 text-xl font-semibold text-label sm:text-2xl">
                    <span className="truncate">{data.project.auftragsnummer || `#${orderId}`}</span>
                    <OrdnerJahrBadge project={data.project} onMoved={() => void refetch()} />
                  </h2>
                  <p className="text-sm text-label-secondary">
                    {[data.project.kunde, data.project.ort].filter(Boolean).join(" · ") || "—"}
                  </p>
                </div>
                {(() => {
                  const workflow = projectWorkflowStatus(data.project);
                  const exc = String(data.project.execution_class || "")
                    .trim()
                    .toUpperCase();
                  return (
                    <span className="eh-wf-badge-stack items-end">
                      <span
                        className={clsx(
                          "text-xs",
                          projectWorkflowStatusBadgeClass(workflow.key),
                        )}
                      >
                        {workflow.label}
                      </span>
                      {workflow.secondary ? (
                        <span
                          className={clsx(
                            "text-xs",
                            projectWorkflowStatusBadgeClass(workflow.secondary.key),
                          )}
                        >
                          {workflow.secondary.label}
                        </span>
                      ) : null}
                      {exc === "EXC2" ? (
                        <span className={clsx("text-xs", projectExecutionClassBadgeClass(exc))}>
                          EXC2
                        </span>
                      ) : null}
                    </span>
                  );
                })()}
              </div>
              <p className="mt-3 max-w-3xl text-sm text-label-secondary">
                {data.project.beschreibung?.trim() || "Keine Beschreibung hinterlegt."}
              </p>
              <div className="mt-3 empty:hidden">
                <ProjectKorrekturBanner project={data.project} />
              </div>
              {isBuero || canManageExc ? (
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
                  {isBuero ? (
                    <ProjectWorkflowStatusControl
                      project={data.project}
                      canEdit
                      showBadges={false}
                      selectLabel="Status ändern"
                      onSaved={() => void refetch()}
                    />
                  ) : null}
                  {canManageExc ? (
                    <ProjectExecutionClassControl
                      project={data.project}
                      canEdit
                      onSaved={() => void refetch()}
                    />
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="eh-project-page-shell">
              <ProjectDetailNav
                active={effectiveTab}
                onChange={setActiveTab}
                counts={navCounts}
                includeNotesTab={isMobile}
                includeBueroTabs={isBuero}
              />

              <div className="eh-project-page-main min-w-0">{tabContent}</div>

              {!isMobile && effectiveTab !== "notizen" ? (
                <aside className="eh-project-page-notes">{panels.notes}</aside>
              ) : null}
            </div>
          </>
        ) : null}
      </AppShell>

      <EditProjectDialog
        open={editOpen}
        orderId={orderId}
        onClose={() => setEditOpen(false)}
        onSaved={() => void refetch()}
      />
    </>
  );
}
